// eslint-disable-next-line import/no-cycle
import { InputGroupField } from '@/types';
// eslint-disable-next-line import/no-cycle
import {
  TemplateEditorState as state,
  updateFormFieldConfig,
  getFormContactProperty,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { getTemplateIframeDocument } from '../utils/helpers';
// eslint-disable-next-line import/no-cycle
import { sanitizeTranslationMarkup } from '../utils/translate';

export const getFields = (selector: string): string[] => {
  const template = getTemplateIframeDocument();
  const fields: HTMLElement[] = Array.from(template?.querySelectorAll(selector));

  return fields
    .filter((field: HTMLElement) => !field.classList.contains('spm_hidden')
      || field.querySelector('.spm_form_input')?.getAttribute('id') === 'spm_form_registration_password') // keep not hidden fields and password
    .map((field: HTMLElement) => field.querySelector('.spm_form_input')?.getAttribute('id') as string); // return ids
};

export const getFieldLabels = (selector: string): InputGroupField[] => {
  const template = getTemplateIframeDocument();
  const fields: HTMLElement[] = Array.from(template?.querySelectorAll(selector));

  return fields
    .filter((field: HTMLElement) => !field.classList.contains('spm_hidden')
      || field.querySelector('.spm_form_input')?.getAttribute('id') === 'spm_form_registration_password') // keep not hidden fields and password
    .map((field: HTMLElement) => {
      const placeholder: string = field.querySelector('.spm_form_input')?.getAttribute('placeholder')?.replace('*', '') as string;
      const label = `templateBuilder.fields.${field.querySelector('.spm_form_input')?.getAttribute('id')?.replace('spm_form_registration_', '').replace('_', '')}`;
      const result: InputGroupField = {
        key: field.querySelector('.spm_form_input')?.getAttribute('id') ?? '',
        label,
        value: placeholder,
      };
      if (/*!field.classList.contains('spm_form_field_base')
        && */field.querySelector('.spm_form_input')?.getAttribute('id') !== 'spm_form_registration_password') { // Display required checkbox only on not mandatory fields
        result.required = field.querySelector('.spm_form_input')?.classList.contains('spm_form_required');
      }
      return result;
    });
};

export const getMessage = (selector: string): string => {
  const template = getTemplateIframeDocument();
  const container: HTMLElement = template?.querySelector(selector) as HTMLElement;
  return container.querySelector('.spm_step2')?.innerHTML ?? '';
};

export const getContactProperty = (selector: string): string => {
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    const formContactPropertyValue = getFormContactProperty(elementId[1]);
    if (formContactPropertyValue) {
      return formContactPropertyValue;
    }
    const template = getTemplateIframeDocument();
    const element: HTMLElement | null = template?.querySelector(selector);
    if (element) {
      const fromAttribute = element.getAttribute('data-contactproperty');
      if (fromAttribute) {
        return fromAttribute;
      }
    }
  }
  return '';
};

export const getDateFormat = (selector: string, name: string) => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    const widget = template.querySelector(`#${elementId[1]}`);
    if (widget) {
      return widget.getAttribute(name) || '';
    }
  }
  return '';
};

export const getCheckboxOptionsProperty = (selector: string, name: string): any[] => {
  if (state.template) {
    const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
    if (elementId && elementId.length) {
      const template = getTemplateIframeDocument();
      const existingConfig = state.template.configuration?.forms?.[elementId[1]]?.configs?.[name];
      if (template && !existingConfig) {
        const options: any[] = [];
        const elements = Array.from(template.querySelectorAll(selector));
        elements.forEach((element) => {
          const inputElement = element.querySelector('input');
          const labelElement = element.querySelector('label') as HTMLElement;
          options.push({
            optionId: inputElement?.id,
            optionName: inputElement?.getAttribute('name') || '',
            optionValue: inputElement?.getAttribute('value') || '',
            optionLabel: sanitizeTranslationMarkup(labelElement).innerHTML.replace(/\n/gi, '').replace(/<br\s*\/?>/gi, '\n').trim(),
          });
        });
        updateFormFieldConfig(elementId[1], name, options);
      }
      return state.template.configuration?.forms?.[elementId[1]]?.configs?.[name] || [];
    }
  }
  return [];
};

export const getDisclosureChannels = (selector: string): string[] => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    // Check state first
    const existingConfig = state.template?.configuration?.forms?.[elementId[1]]?.configs?.['data-disclosure-channels'];
    if (existingConfig) {
      return String(existingConfig).split(',').filter(Boolean);
    }
    // Fallback: read from DOM and persist to state
    const widget = template?.querySelector(`#${elementId[1]}`);
    if (widget) {
      const attr = widget.getAttribute('data-disclosure-channels');
      if (attr) {
        updateFormFieldConfig(elementId[1], 'data-disclosure-channels', attr);
        return attr.split(',').filter(Boolean);
      }
    }
  }
  return [];
};

export const getConsentChannel = (selector: string): string => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    // Check state first
    const existingConfig = state.template?.configuration?.forms?.[elementId[1]]?.configs?.['data-consent-channel'];
    if (existingConfig) {
      return String(existingConfig);
    }
    // Fallback: read from DOM
    const widget = template?.querySelector(`#${elementId[1]}`);
    if (widget) {
      const attr = widget.getAttribute('data-consent-channel');
      if (attr) {
        updateFormFieldConfig(elementId[1], 'data-consent-channel', attr);
        return attr;
      }
    }
  }
  return '';
};

/**
 * CNIL pixels — état du toggle « suivi groupé ». Lit configs.trackingConsent, avec
 * fallback sur l'attribut DOM data-tracking-consent qu'il persiste (comme getConsentChannel).
 * Retourne 'true'/'false' pour le composant CHECKBOX. Widgets existants (sans attr) → 'false'
 * (rétro-compat : pas de collecte de suivi tant que non activée).
 */
export const getTrackingConsent = (selector: string): string => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    const existing = state.template?.configuration?.forms?.[elementId[1]]?.configs?.trackingConsent;
    if (existing !== undefined && existing !== null) {
      return existing === true || existing === 'true' ? 'true' : 'false';
    }
    const widget = template?.querySelector(`#${elementId[1]}`);
    if (widget) {
      const attr = widget.getAttribute('data-tracking-consent');
      if (attr) {
        const enabled = attr === 'true';
        updateFormFieldConfig(elementId[1], 'trackingConsent', enabled);
        return enabled ? 'true' : 'false';
      }
    }
  }
  return 'false';
};

/**
 * CNIL pixels — etat du mode de consentement : 'true' si granulaire (« Gerer finement »),
 * 'false' si groupe (defaut). Lit configs.consentMode, avec FALLBACK sur l'attribut DOM
 * data-consent-mode qu'il persiste (comme getTrackingConsent) — sinon, a la reouverture d'un
 * widget deja granulaire, le toggle apparait DECOCHE car configs n'est pas (re)peuple en state.
 */
export const getConsentMode = (selector: string): string => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    const mode = state.template?.configuration?.forms?.[elementId[1]]?.configs?.consentMode;
    if (mode !== undefined && mode !== null) {
      return mode === 'granular' ? 'true' : 'false';
    }
    const widget = template?.querySelector(`#${elementId[1]}`);
    if (widget) {
      const attr = widget.getAttribute('data-consent-mode');
      if (attr) {
        const isGranular = attr === 'granular';
        updateFormFieldConfig(elementId[1], 'consentMode', isGranular ? 'granular' : 'grouped');
        return isGranular ? 'true' : 'false';
      }
    }
  }
  return 'false';
};

/**
 * Case de consentement obligatoire : 'true' si le formulaire ne peut pas être envoyé sans la
 * cocher. Lit configs.consentRequired, avec repli sur l'attribut DOM data-consent-required
 * (lu par forms.js). Widgets existants (sans attribut) → 'false' : facultative par défaut.
 */
export const getConsentRequired = (selector: string): string => {
  const template = getTemplateIframeDocument();
  const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
  if (elementId && elementId.length) {
    const existing = state.template?.configuration?.forms?.[elementId[1]]?.configs?.consentRequired;
    if (existing !== undefined && existing !== null) {
      return existing === true || existing === 'true' ? 'true' : 'false';
    }
    const widget = template?.querySelector(`#${elementId[1]}`);
    if (widget) {
      const attr = widget.getAttribute('data-consent-required');
      if (attr) {
        const required = attr === 'true';
        updateFormFieldConfig(elementId[1], 'consentRequired', required);
        return required ? 'true' : 'false';
      }
    }
  }
  return 'false';
};

export const getSelectOptionsProperty = (selector: string, name: string): any[] => {
  if (state.template) {
    const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(selector);
    if (elementId && elementId.length) {
      const template = getTemplateIframeDocument();
      const existingConfig = state.template.configuration?.forms?.[elementId[1]]?.configs?.[name];
      if (template && !existingConfig) {
        const options: any[] = [];
        const selectElement = template.querySelector(selector);
        if (selectElement) {
          // Support both old select/option and new custom select structure
          const optionsElement = Array.from(selectElement.querySelectorAll('.spm_custom_select__option, option:not(.spm_widget_input_select__placeholder)'));
          optionsElement.forEach((optionElement) => {
            options.push({
              optionId: optionElement.id,
              optionName: '',
              optionValue: optionElement.getAttribute('data-value') || optionElement.getAttribute('value') || '',
              optionLabel: optionElement.getAttribute('data-selectoptionlabel') || '',
            });
          });
        }
        updateFormFieldConfig(elementId[1], name, options);
      }
      return state.template.configuration?.forms?.[elementId[1]]?.configs?.[name] || [];
    }
  }
  return [];
};
